import type { ComponentProps } from 'react';
import type ProductCardsMessageImpl from './ProductCardsMessage';
import type OrderCardsMessageImpl from './OrderCardsMessage';
import type FormMessageImpl from './FormMessage';
import type CardMessageImpl from './CardMessage';
import type SourceCitationImpl from './SourceCitation';
import type ThinkingDisplayImpl from './ThinkingDisplay';
import type ToolCallDisplayImpl from './ToolCallDisplay';
import { lazyWithPreload, type PreloadableComponent } from '../../utils/lazyWithPreload';

/*
 * Rich message renderers that only some replies need (product / order cards,
 * forms, cards, citations, reasoning, tool details). They share one `rich-messages` chunk that
 * MainContent preloads when the panel opens, so by the time a reply needs one it
 * renders synchronously; `MessageBubble` wraps each in `Suspense fallback={null}`
 * for the rare case the chunk is still in flight (e.g. restored history).
 */
export const ProductCardsMessage: PreloadableComponent<
  ComponentProps<typeof ProductCardsMessageImpl>
> = lazyWithPreload(() => import(/* webpackChunkName: "rich-messages" */ './ProductCardsMessage'));
export const OrderCardsMessage: PreloadableComponent<ComponentProps<typeof OrderCardsMessageImpl>> =
  lazyWithPreload(() => import(/* webpackChunkName: "rich-messages" */ './OrderCardsMessage'));
export const FormMessage: PreloadableComponent<ComponentProps<typeof FormMessageImpl>> =
  lazyWithPreload(() => import(/* webpackChunkName: "rich-messages" */ './FormMessage'));
export const CardMessage: PreloadableComponent<ComponentProps<typeof CardMessageImpl>> =
  lazyWithPreload(() => import(/* webpackChunkName: "rich-messages" */ './CardMessage'));
export const SourceCitation: PreloadableComponent<ComponentProps<typeof SourceCitationImpl>> =
  lazyWithPreload(() => import(/* webpackChunkName: "rich-messages" */ './SourceCitation'));
export const ThinkingDisplay: PreloadableComponent<ComponentProps<typeof ThinkingDisplayImpl>> =
  lazyWithPreload(() => import(/* webpackChunkName: "rich-messages" */ './ThinkingDisplay'));
export const ToolCallDisplay: PreloadableComponent<ComponentProps<typeof ToolCallDisplayImpl>> =
  lazyWithPreload(() => import(/* webpackChunkName: "rich-messages" */ './ToolCallDisplay'));

const ALL = [
  ProductCardsMessage,
  OrderCardsMessage,
  FormMessage,
  CardMessage,
  SourceCitation,
  ThinkingDisplay,
  ToolCallDisplay,
];

/** Loads the `rich-messages` chunk ahead of need. Never rejects. */
export function preloadRichMessages(): Promise<void> {
  return Promise.all(ALL.map(component => component.preload())).then(
    () => undefined,
    () => undefined
  );
}
